import { FormConfig, ModalButton } from 'src/app/shared/models/dynamic.model';
import { HistoryTableData, TabConfig } from './air-waybill.model';
import { ValidatorPattern } from 'src/app/shared/utility/validators.pattern';

export const tabs: TabConfig[] = [
  { label: 'AIR WAYBILL', path: 'air-waybill-gen-form-tab' },
  { label: 'ADDRESS BOOK', path: 'address-book-tab' },
  { label: 'HISTORY', path: 'history-tab' },
];

/** Section titles shown in the Generate Air Waybill validation popup (IBC parity). */
export const airWaybillMandatoryValidationSections = {
  shipperConsignee: 'Shipper & Consignee Section',
  shipmentInformation: 'Shipment Information Section',
  pieceDetails: 'Piece Details Section',
  dutiableCommodity: 'Dutiable Shipments Commodity Section',
} as const;

export const shipperInfoFormFieldConfig: FormConfig = {
  fields: [
    {
      id: 'name',
      type: 'input',
      label: 'Shipper Name',
      required: true,
      requiredError: 'Shipper Name is required',
      noInitialWhiteSpace: true,
      minValue: 1,
      maxValue: 50,
      isCheckForMaxValue: true,
    },
    {
      id: 'nameId',
      type: 'extra',
    },
    {
      id: 'accountNumber',
      type: 'input',
      label: 'Shipper Account',
      requiredError: 'Shipper Account is required',
      allowNumeric: true,
      maxValue: 7,
      isCheckForMaxValue: true,
    },
    {
      id: 'companyName',
      type: 'input',
      label: 'Company Name',
      noInitialWhiteSpace: true,
      minValue: 1,
      maxValue: 50,
      isCheckForMaxValue: true,
    },
    {
      id: 'countryCode',
      required: true,
      type: 'input',
      label: 'Country Code',
      requiredError: 'Country Code is required',
    },
    {
      type: 'input',
      label: 'Address Line 1',
      id: 'address_1',
      required: true,
      requiredError: 'Address Line 1 is required',
      noInitialWhiteSpace: true,
      minValue: 1,
      maxValue: 126,
      isCheckForMaxValue: true,
    },
    {
      type: 'input',
      label: 'Address Line 2',
      id: 'address_2',
      noInitialWhiteSpace: true,
      maxValue: 126,
      isCheckForMaxValue: true,
    },
    {
      id: 'state',
      type: 'input',
      label: 'State / Region',
      requiredError: 'State / Region is required',
    },
    {
      type: 'input',
      label: 'City',
      requiredError: 'City is required',
      id: 'city',
      required: true,
      minValue: 1,
      maxValue: 126,
      allowAlphabetWithHyphenSpace: true,
    },
    {
      id: 'zipCode',
      type: 'input',
      label: 'Zip Code',
      minError: 'Zip code must be between 4 and 10 characters',
      requiredError: 'Zip Code is required',
      maxValue: 10,
      minValue: 4,
      noInitialWhiteSpace: true,
      isCheckForMaxValue: true,
    },
    {
      id: 'phone',
      label: 'Phone',
      required: true,
      requiredError: 'Phone is required',
      type: 'input',
    },
  ],
};

export const consigneeInfoFormFieldConfig: FormConfig = {
  fields: [
    {
      id: 'name',
      type: 'input',
      required: true,
      requiredError: 'Name is required',
      label: 'Name',
      noInitialWhiteSpace: true,
      minValue: 1,
      maxValue: 50,
      isCheckForMaxValue: true,
    },
    {
      id: 'nameId',
      type: 'extra',
    },
    {
      id: 'companyName',
      type: 'input',
      label: 'Company Name',
      noInitialWhiteSpace: true,
      minValue: 1,
      maxValue: 50,
      isCheckForMaxValue: true,
    },
    {
      id: 'countryCode',
      required: true,
      type: 'input',
      label: 'Country Code',
      requiredError: 'Country Code is required',
    },
    {
      type: 'input',
      id: 'address_1',
      requiredError: 'Address Line 1 is required',
      required: true,
      label: 'Address Line 1',
      noInitialWhiteSpace: true,
      minValue: 1,
      maxValue: 126,
      isCheckForMaxValue: true,
    },
    {
      label: 'Address Line 2',
      type: 'input',
      id: 'address_2',
      noInitialWhiteSpace: true,
      maxValue: 126,
      isCheckForMaxValue: true,
    },
    {
      type: 'input',
      requiredError: 'State / Region is required',
      id: 'state',
      label: 'State / Region',
    },
    {
      type: 'input',
      id: 'city',
      label: 'City',
      required: true,
      requiredError: 'City is required',
      minValue: 1,
      maxValue: 126,
      allowAlphabetWithHyphenSpace: true,
    },
    {
      id: 'zipCode',
      label: 'Zip Code',
      type: 'input',
      requiredError: 'Zip Code is required',
      minError: 'Zip code must be between 4 and 10 characters',
      maxValue: 10,
      minValue: 4,
      noInitialWhiteSpace: true,
      isCheckForMaxValue: true,
    },
    {
      id: 'phone',
      label: 'Telephone',
      required: true,
      requiredError: 'Telephone is required',
      type: 'input',
    },
    {
      id: 'email',
      type: 'input',
      label: 'Email',
      required: true,
      requiredError: 'Email is required',
      maxValue: 50,
      isCheckForMaxValue: true,
      pattern: ValidatorPattern.Email_Validator,
      emailPatternError: 'Invalid Email',
      noInitialWhiteSpace: true,
    },
    {
      id: 'taxId',
      label: 'Tax ID',
      type: 'input',
      noInitialWhiteSpace: true,
      maxValue: 126,
      isCheckForMaxValue: true,
      requiredError: 'Tax ID is mandatory for all non-document shipments going to Brazil',
    },
  ],
};

export const shipperConsigneeAddressFormFieldConfig: FormConfig = {
  fields: [
    {
      id: 'ibcAccount',
      type: 'input',
      label: 'IBC Account',
      requiredError: 'IBC Account is required',
      allowNumeric: true,
      maxValue: 7,
      isCheckForMaxValue: true,
    },
    {
      id: 'name',
      type: 'input',
      label: 'Name',
      required: true,
      maxValue: 50,
      requiredError: 'Name is required',
      allowAlphaNumericWithSpace: true,
      isCheckForMaxValue: true,
    },
    {
      id: 'displayId',
      type: 'input',
      label: 'Display ID',
      required: true,
      requiredError: 'Display ID is required',
      allowAlphaNumericWithSpace: true,
      maxValue: 50,
      isCheckForMaxValue: true,
    },
    {
      id: 'companyName',
      label: 'Company Name',
      required: true,
      allowAlphaNumericWithSpace: true,
      requiredError: 'Company Name is required',
      type: 'input',
      maxValue: 50,
      isCheckForMaxValue: true,
    },
    {
      id: 'countryCode',
      label: 'Country Code',
      type: 'input',
      required: true,
      requiredError: 'Country Code is required',
    },
    {
      type: 'input',
      label: 'Country',
      id: 'country',
      isDisableField: true,
    },
    {
      id: 'address_1',
      type: 'input',
      label: 'Address Line 1',
      required: true,
      requiredError: 'Address Line 1 is required',
      maxValue: 126,
      isCheckForMaxValue: true,
    },
    {
      id: 'address_2',
      type: 'input',
      label: 'Address Line 2',
      maxValue: 126,
      isCheckForMaxValue: true,
    },
    {
      id: 'state',
      label: 'State / Region',
      type: 'input',
    },
    {
      label: 'City',
      id: 'city',
      required: true,
      requiredError: 'City is required',
      type: 'input',
      maxValue: 50,
      isCheckForMaxValue: true,
      allowAlphabetWithHyphenSpace: true,
    },
    {
      id: 'zipCode',
      label: 'Zip / Postal Code',
      type: 'input',
      minError: 'Zip code must be between 4 and 10 characters',
      requiredError: 'Zip Code is required',
      maxValue: 10,
      minValue: 4,
      noInitialWhiteSpace: true,
      isCheckForMaxValue: true,
    },
    {
      id: 'phone',
      type: 'input',
      label: 'Telephone',
      required: true,
      requiredError: 'Telephone is required',
    },
    {
      id: 'email',
      type: 'input',
      required: true,
      requiredError: 'Email is required',
      label: 'Email',
      maxValue: 50,
      isCheckForMaxValue: true,
      emailPatternError: 'Invalid Email',
      pattern: ValidatorPattern.Email_Validator,
      noInitialWhiteSpace: true,
    },
    {
      id: 'taxId',
      type: 'input',
      label: 'Tax ID',
      allowAlphaNumeric: true,
      isCheckForMaxValue: true,
      maxValue: 50,
      allowAlphabetWithSpace: true,
    },
    {
      id: 'id',
      type: 'extra',
    },
  ],
};

export const shipmentInfoFormConfig: FormConfig = {
  fields: [
    {
      id: 'type',
      type: 'select',
      label: 'Shipment Type',
      required: true,
      requiredError: 'Shipment Type is required',
    },
    {
      id: 'deliveryTerms',
      type: 'select',
      label: 'Delivery Terms',
      required: true,
      requiredError: 'Delivery Terms is required',
    },
    {
      id: 'reference',
      type: 'input',
      label: 'Reference',
      maxValue: 126,
      isCheckForMaxValue: true,
    },
    {
      id: 'serviceType',
      type: 'extra',
    },
    {
      id: 'serviceTypeName',
      type: 'select',
      label: 'Service Type',
      required: true,
      requiredError: 'Service Type is required',
    },
    {
      id: 'freight',
      type: 'input',
      label: 'Freight',
    },
  ],
};

export const commodityInfoFormConfig: FormConfig = {
  fields: [
    {
      id: 'description',
      type: 'input',
      label: 'Description',
      maxValue: 126,
      isCheckForMaxValue: true,
      required: true,
      requiredError: 'Description is required',
    },
    {
      id: 'hsCode',
      type: 'input',
      label: 'HTS Code',
      noInitialWhiteSpace: true,
      allowNumeric: true,
      minValue: 6,
      maxValue: 10,
      minError: 'HTS Code should be of 6-10 numeric digits only',
    },
    {
      id: 'value',
      type: 'input',
      label: 'Total Value ($)',
      requiredError: 'Total Value is required',
      allowNumeric: true,
      required: true,
    },
    {
      id: 'weight',
      type: 'input',
      label: 'Total Weight',
      requiredError: 'Total weight is required',
      allowNumeric: true,
      required: true,
    },
    {
      id: 'quantity',
      type: 'input',
      label: 'Quantity',
      allowNumeric: true,
      required: true,
    },
    {
      id: 'countryCode',
      type: 'select',
      label: 'Country',
    },
    {
      id: 'itemNumber',
      type: 'input',
      label: 'Item Number',
      maxLength: 126,
      allowAlphaNumeric: true,
    },
    {
      id: 'action',
      type: 'extra',
    },
    {
      id: 'commodityNumber',
      type: 'extra',
    },
  ],
};

export const commodityInfoGridColumnConfig = [
  { key: 'description', label: 'Description' },
  { key: 'hsCode', label: 'HTS Code' },
  { key: 'value', label: 'Total Value ($)' },
  { key: 'weight', label: 'Total Weight' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'countryCode', label: 'Country' },
  { key: 'itemNumber', label: 'Item Number' },
];

export const pieceInfoFormConfig: FormConfig = {
  fields: [
    {
      id: 'weight',
      type: 'input',
      label: 'Piece Weight',
      placeholder: 'Weight is mandatory for each piece',
      required: true,
      requiredError: 'Piece Weight is required',
    },
    {
      id: 'dimL',
      type: 'input',
      placeholder: 'Length',
      required: false,
    },
    {
      id: 'dimW',
      type: 'input',
      placeholder: 'Width',
      required: false,
    },
    {
      id: 'dimH',
      type: 'input',
      placeholder: 'Height',
      required: false,
    },
    {
      id: 'multiplier',
      type: 'input',
      placeholder: 'How many similar?',
      label: 'Piece Multiplier',
      required: true,
      requiredError: 'Piece Multiplier is required',
      allowNumberWithFirstNonZeroNumber: true,
    },
    {
      id: 'volumetric',
      type: 'extra',
    },
    {
      id: 'action',
      type: 'extra',
    },
    {
      id: 'orderNumber',
      type: 'extra',
    },
  ],
};

export const pieceInfoGridColumnConfig = [
  { key: 'weight', label: 'Weight (lbs)' },
  { key: 'dimensions', label: 'Dimensions (Inches)' },
  { key: 'volumetric', label: 'Volumetric (lbs)' },
  { key: 'multiplier', label: 'Count' },
  { key: 'calculated', label: 'Calculated' },
];

export const mapLabelKeyToLabels: Record<string, string> = {
  billNumber: 'Bill Number',
  carrier: 'Carrier',
  countryOfOrigin: 'Country Of Origin',
  currency: 'Currency',
  limitationToLiability: 'Limitation of Liability',
  date: 'Date',
  finalDestination: 'Final Destination',
  invoice: 'Invoice',
  notes: 'Notes',
  poNumber: 'PO Number',
  contents: 'Contents',
  pieces: 'Pieces',
  reference: 'Reference',
  service: 'Service Type',
  createdDateAndTime: 'Created',
  description: 'Description',
  hts: 'HTS',
  value: 'Value ($)',
  weight: 'Weight (lbs)',
  righToInspect: 'Render to Inspect',
};

export const commercialInvoicePopupActionButtons: ModalButton[] = [
  {
    id: 'cancel',
    label: 'Finish',
    class: 'mat-button-md ibc-secondary-dark-button',
  },
  {
    id: 'print',
    label: 'Print',
    class: 'mat-button-md ibc-primary-dark-button-md',
  },
];

export const logoutPopupActionButtons: ModalButton[] = [
  {
    id: 'ok',
    label: 'OK',
    class: 'mat-button-md ibc-primary-dark-button-md',
  },
];

export const phoneCountryMismatchPopupButtons: ModalButton[] = [
  {
    id: 'cancel',
    label: 'Cancel',
    class: 'mat-button-md ibc-secondary-dark-button',
  },
  {
    id: 'continue',
    label: 'Continue',
    class: 'mat-button-md ibc-primary-dark-button-md',
  },
];

export const countryChangeConfirmPopupButtons: ModalButton[] = [
  {
    id: 'cancel',
    label: 'Cancel',
    class: 'mat-button-md ibc-secondary-dark-button',
  },
  {
    id: 'continue',
    label: 'Change Country',
    class: 'mat-button-md ibc-primary-dark-button-md',
  },
];

export const addressBookSavePromptButtons: ModalButton[] = [
  {
    id: 'no',
    label: 'No',
    class: 'mat-button-md ibc-secondary-dark-button',
  },
  {
    id: 'yes',
    label: 'Yes',
    class: 'mat-button-md ibc-primary-dark-button-md',
  },
];

export const termsAndConditionsForGenAirWaybillModal = {
  righToInspect:
    'Your shipment may, at our option or at the request of governmental authorities, be opened, searched and inspected by us or such authorities at any time.',
  limitationToLiability:
    'In consideration of the transportation charges for the movement of any shipment, it is agreed that the liability of IBC shall be limited, in any event, to the lower sum of U.S. $100.00 or the actual value of the documents or shipment, unless you declare a higher value on this Air Waybill and additional insurance coverage is arranged and paid for in advance. Actual value shall be determined at the time and place IBC accepted the shipment and shall exclude any special value to the shipper or any other person. The actual value of documents shall be the cost of replacing or reconstructing the documents. The actual value of packages shall be the lower of the cost of repairing or replacing the contents or the resale or fair market value of the parcel. The Warsaw Convention may further limit our liability.',
};

export const historyTableColumnConfig = [
  { key: 'date', label: 'Date' },
  { key: 'trackLink', label: 'Track Link' },
  { key: 'consginee', label: 'Consignee' },
  { key: 'commercialInvoice', label: 'Commercial Invoice' },
  { key: 'airWaybills', label: 'Air Waybills' },
];

export const historyTableData: HistoryTableData[] = [];

export const preferredShipmentDetailsOrder = [
  'createdDateAndTime',
  'contents',
  'value',
  'service',
  'pieces',
  'weight',
  'description',
  'hts',
  'reference',
];

export const preferredInvoiceDetailsOrder = [
  'date',
  'currency',
  'billNumber',
  'invoice',
  'poNumber',
  'countryOfOrigin',
  'finalDestination',
  'carrier',
  'notes',
];

export type PartyTypes = 'shipper' | 'consignee' | 'commodity';

export const airWaybillFormFieldNames = {
  shipper: 'shipper',
  consignee: 'consignee',
  phone: 'phone',
  taxId: 'taxId',
  countryCode: 'countryCode',
  state: 'state',
  zipCode: 'zipCode',
  type: 'type',
  deliveryTerms: 'deliveryTerms',
  description: 'description',
  city: 'city',
} as const;

export type ShipperConsigneeType = Extract<PartyTypes, 'shipper' | 'consignee'>;
